// Auto-fit big numbers to their container.
//
// Large money values (TSh920,359,432.76) cannot wrap, so on narrow screens
// they overflow stat cards. This watches the document for prominent text
// elements (text-lg … text-4xl) whose single-line content is wider than the
// box and shrinks the font-size (down to 45%) until it fits; it grows back
// when the box widens or the value gets shorter.
const CANDIDATE_SELECTOR = [
  '[class*="text-lg"]', '[class*="text-xl"]', '[class*="text-2xl"]',
  '[class*="text-3xl"]', '[class*="text-4xl"]', '[class*="text-5xl"]',
].join(',');
const NUMERIC_RE = /\d[\d,.\s]*/;
const MIN_SCALE = 0.45;
const touched = new Set();
let scheduled = false;
let started = false;

const isLeafish = (el) => {
  for (const child of el.children) {
    if (child.tagName !== 'SPAN' && child.tagName !== 'SUP' && child.tagName !== 'SUB' && child.tagName !== 'SMALL') return false;
  }
  return true;
};

const fitOne = (el) => {
  if (!el.isConnected) { touched.delete(el); return; }
  if (el.style.fontSize) el.style.fontSize = '';
  const text = (el.textContent || '').trim();
  if (!text || text.length > 40 || !NUMERIC_RE.test(text) || /\s\S+\s\S+\s/.test(text)) {
    touched.delete(el);
    return;
  }
  const avail = el.clientWidth;
  const need = el.scrollWidth;
  if (!avail || need <= avail + 1) {
    if (touched.has(el)) { touched.delete(el); el.removeAttribute('data-autofit'); }
    return;
  }
  const natural = parseFloat(window.getComputedStyle(el).fontSize) || 16;
  const scale = Math.max(MIN_SCALE, (avail / need) * 0.98);
  el.style.fontSize = `${(natural * scale).toFixed(2)}px`;
  el.setAttribute('data-autofit', scale.toFixed(2));
  if (!el.getAttribute('title')) el.setAttribute('title', text);
  touched.add(el);
};

const runScan = () => {
  scheduled = false;
  const seen = new Set();
  touched.forEach((el) => { seen.add(el); fitOne(el); });
  document.querySelectorAll(CANDIDATE_SELECTOR).forEach((el) => {
    if (seen.has(el) || !isLeafish(el)) return;
    if (el.scrollWidth > el.clientWidth + 1) fitOne(el);
  });
};

const schedule = () => {
  if (scheduled) return;
  scheduled = true;
  window.requestAnimationFrame(() => setTimeout(runScan, 60));
};

export function startAutoFitNumbers() {
  if (started || typeof window === 'undefined' || typeof MutationObserver === 'undefined') return () => {};
  started = true;
  const mo = new MutationObserver(schedule);
  mo.observe(document.body, { childList: true, subtree: true, characterData: true });
  window.addEventListener('resize', schedule);
  document.fonts?.ready?.then?.(schedule);
  schedule();
  return () => {
    mo.disconnect();
    window.removeEventListener('resize', schedule);
    started = false;
  };
}
